import { useEffect } from "react";

const BASE_TITLE = "Complaudax";

/**
 * Sticky section chrome for every dashboard page.
 *
 * It also names the browser tab, "Complaudax | Dashboard". The section title is
 * already the one true name of the page, so deriving the tab from it here keeps
 * the two in step and spares every page from repeating itself.
 *
 * The title block, and the tab bar when a section has one, stay pinned to the
 * top of the viewport while only the content underneath scrolls. Passing the
 * tabs in as children (rather than letting each page render its own bar under
 * the header) is what keeps them inside the sticky band.
 *
 * The negative margins cancel <main>'s padding so the band spans the full
 * content width: without them the background would stop short of the gutters
 * and rows would be visible sliding past on either side.
 *
 * Usage:
 *   <PageHeader title="Sites" description="..." actions={<button .../>}>
 *     <Tabs ... />          // optional
 *   </PageHeader>
 */
export const PageHeader = ({ title, description, actions, above, children, testId }) => {
  useEffect(() => {
    document.title = title ? `${BASE_TITLE} | ${title}` : BASE_TITLE;
    // Login and the payment returns render no header of their own, so the tab
    // falls back to the product name instead of keeping the name of the page
    // the user just left.
    return () => { document.title = BASE_TITLE; };
  }, [title]);

  return (
    <div
      data-testid={testId}
      className="sticky top-0 z-20 -mx-8 -mt-8 mb-6 px-8 pt-8 bg-[#F8F9FA] border-b border-gray-200"
    >
      {above}
      <div className="flex items-start justify-between gap-4 pb-5">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold mb-1">{title}</h1>
          {description && <div className="text-sm text-ink-muted">{description}</div>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
};
